import { useState } from 'react';
import { Edit, Trash2, FileText, File } from 'lucide-react';
import { ChargeImagesSection } from './ChargeImagesSection';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import type { JobCharge } from '../types/jobCharge';

interface ChargeListTableProps {
  jobId: number;
  charges: JobCharge[];
  onEdit: (charge: JobCharge) => void;
  onDelete: (chargeId: number) => void;
  isDeleting?: boolean;
}

export function ChargeListTable({
  jobId,
  charges,
  onEdit,
  onDelete,
  isDeleting,
}: ChargeListTableProps) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [chargeToDelete, setChargeToDelete] = useState<number | null>(null);
  const [documentsDialogOpen, setDocumentsDialogOpen] = useState(false);
  const [selectedChargeForDocuments, setSelectedChargeForDocuments] =
    useState<JobCharge | null>(null);

  const handleOpenDocuments = (charge: JobCharge) => {
    setSelectedChargeForDocuments(charge);
    setDocumentsDialogOpen(true);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const truncate = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return `${text.substring(0, maxLength)}...`;
  };

  const handleDeleteClick = (chargeId: number) => {
    setChargeToDelete(chargeId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (chargeToDelete !== null) {
      onDelete(chargeToDelete);
      setDeleteDialogOpen(false);
      setChargeToDelete(null);
    }
  };

  // Sort charges by date descending (most recent first)
  const sortedCharges = [...charges].sort((a, b) => {
    return new Date(b.chargeDate).getTime() - new Date(a.chargeDate).getTime();
  });

  // Calculate total
  const total = charges.reduce((sum, charge) => sum + charge.amount, 0);

  if (charges.length === 0) {
    return (
      <div className="bg-white rounded-[20px] border border-[#d9d9d9] p-8 text-center">
        <p className="text-gray-500">No charges recorded yet.</p>
        <p className="text-sm text-gray-400 mt-1">
          Click "Add Charge" to record your first charge.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white rounded-[20px] border border-[#d9d9d9] overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[120px]">Date</TableHead>
              <TableHead>Description</TableHead>
              <TableHead className="text-right w-[150px]">Amount</TableHead>
              <TableHead className="w-[100px] text-center">Documents</TableHead>
              <TableHead className="w-[100px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedCharges.map((charge) => (
              <TableRow key={charge.id}>
                <TableCell className="text-sm">
                  {formatDate(charge.chargeDate)}
                </TableCell>
                <TableCell className="text-sm text-gray-600">
                  {charge.description
                    ? truncate(charge.description, 60)
                    : '-'}
                </TableCell>
                <TableCell
                  className={`text-right text-sm font-medium ${
                    charge.amount < 0 ? 'text-red-600' : ''
                  }`}
                >
                  {formatCurrency(charge.amount)}
                </TableCell>
                <TableCell className="text-center">
                  {charge.imageCount > 0 ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleOpenDocuments(charge)}
                      className="h-8 w-8 p-0"
                      aria-label={`View ${charge.imageCount} document${charge.imageCount !== 1 ? 's' : ''} for charge`}
                    >
                      <FileText className="h-5 w-5 text-[#0A0A9E]" />
                    </Button>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleOpenDocuments(charge)}
                      className="h-8 w-8 p-0"
                      aria-label="Add documents for charge"
                    >
                      <File className="h-5 w-5 text-gray-300" />
                    </Button>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onEdit(charge)}
                      className="h-8 w-8 p-0"
                      aria-label="Edit charge"
                    >
                      <Edit className="h-4 w-4 text-[#0A0A9E]" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteClick(charge.id)}
                      className="h-8 w-8 p-0"
                      aria-label="Delete charge"
                      disabled={isDeleting}
                    >
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {/* Summary row */}
            <TableRow className="bg-gray-50 font-semibold">
              <TableCell colSpan={2} className="text-right">
                Total
              </TableCell>
              <TableCell
                className={`text-right ${total < 0 ? 'text-red-600' : ''}`}
              >
                {formatCurrency(total)}
              </TableCell>
              <TableCell colSpan={2}></TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete Charge"
        message="Are you sure you want to delete this charge? This action cannot be undone."
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={handleDeleteConfirm}
      />

      {/* Documents Dialog */}
      <Dialog open={documentsDialogOpen} onOpenChange={setDocumentsDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Charge Documents
              {selectedChargeForDocuments?.description &&
                ` - ${truncate(selectedChargeForDocuments.description, 30)}`}
            </DialogTitle>
          </DialogHeader>
          {selectedChargeForDocuments && (
            <ChargeImagesSection
              jobId={jobId}
              chargeId={selectedChargeForDocuments.id}
              canDelete={true}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
